(() => {
  const API_REVISION = '2026-07-15';
  const POPUP_DELAY = 25000;
  const DISMISS_KEY = 'le_dynamic_signup_popup_dismissed';
  const SUCCESS_KEY = 'le_dynamic_signup_complete';
  const DISMISS_FOR = 5 * 24 * 60 * 60 * 1000;
  const ANALYTICS_RETRY_DELAY = 250;
  const ANALYTICS_MAX_ATTEMPTS = 40;
  const KLAVIYO_SIGNUP_EVENT = 'LE Signup Submitted';
  const KLAVIYO_EVENT_MAX_ATTEMPTS = 3;
  const KLAVIYO_EVENT_RETRY_DELAY = 400;

  const storageGet = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const storageSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      return false;
    }

    return true;
  };

  const message = (root, key) => root.dataset[key] || '';

  const setStatus = (root, text, state = '') => {
    const status = root.querySelector('[data-le-signup-status]');
    if (!status) return null;
    status.textContent = text;
    status.dataset.state = state;
    return status;
  };

  const analyticsPayload = (root, extra = {}) => ({
    form_type: root.dataset.mode,
    signup_language: root.dataset.language,
    signup_country: root.dataset.country,
    signup_currency: root.dataset.currency,
    ...extra
  });

  const publishAnalyticsEvent = async (eventName, payload, attempt = 0) => {
    const publish = window.Shopify?.analytics?.publish;

    if (typeof publish !== 'function') {
      if (attempt < ANALYTICS_MAX_ATTEMPTS) {
        window.setTimeout(
          () => publishAnalyticsEvent(eventName, payload, attempt + 1),
          ANALYTICS_RETRY_DELAY
        );
      } else {
        console.error(`Shopify Analytics unavailable for ${eventName}`);
      }
      return;
    }

    try {
      const published = await publish.call(window.Shopify.analytics, eventName, payload);

      if (!published && attempt < ANALYTICS_MAX_ATTEMPTS) {
        window.setTimeout(
          () => publishAnalyticsEvent(eventName, payload, attempt + 1),
          ANALYTICS_RETRY_DELAY
        );
      } else if (!published) {
        console.error(`Shopify Analytics rejected ${eventName}`);
      }
    } catch (error) {
      if (attempt < ANALYTICS_MAX_ATTEMPTS) {
        window.setTimeout(
          () => publishAnalyticsEvent(eventName, payload, attempt + 1),
          ANALYTICS_RETRY_DELAY
        );
      } else {
        console.error(`Shopify Analytics failed for ${eventName}`, error);
      }
    }
  };

  const trackFormView = (root) => {
    if (root.dataset.analyticsViewTracked === 'true') return;
    root.dataset.analyticsViewTracked = 'true';
    publishAnalyticsEvent('lisa_eleni:newsletter_form_view', analyticsPayload(root));
  };

  const signupProperties = (root, tier, minimumSpend) => ({
    Staffelrabatt: `${tier}%`,
    signup_language: root.dataset.language,
    signup_country: root.dataset.country,
    signup_currency: root.dataset.currency,
    signup_tier: Number(tier),
    signup_minimum_spend: Number(minimumSpend),
    signup_source: root.dataset.mode
  });

  const subscribe = async (root, email, profileProperties) => {
    const url = new URL('https://a.klaviyo.com/client/subscriptions');
    url.searchParams.set('company_id', root.dataset.companyId);

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: `Lisa Eleni dynamic ${root.dataset.mode} signup`,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: profileProperties,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED'
                    }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: root.dataset.listId
            }
          }
        }
      }
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: API_REVISION
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error(`Klaviyo subscription failed (${response.status})`);
  };

  const identifySubscriber = async (email, properties) => {
    const identify = window.LisaEleniKlaviyo?.identify;

    if (typeof identify !== 'function') return false;

    try {
      return await identify(email, properties);
    } catch (error) {
      console.error('Klaviyo browser identification failed', error);
      return false;
    }
  };

  const pageUrlWithoutQuery = () => {
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    return url.toString();
  };

  const createSubmissionId = () => {
    if (typeof window.crypto?.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return `le-signup-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const wait = (duration) =>
    new Promise((resolve) => window.setTimeout(resolve, duration));

  const trackKlaviyoSignup = async (
    root,
    email,
    properties,
    submissionId,
    occurredAt,
    pageUrl,
    attempt = 0
  ) => {
    const canTrack = window.LisaEleniKlaviyo?.canTrack;

    if (typeof canTrack !== 'function' || !canTrack()) {
      root.dataset.klaviyoEventStatus = 'skipped';
      return false;
    }

    const url = new URL('https://a.klaviyo.com/client/events');
    url.searchParams.set('company_id', root.dataset.companyId);

    const payload = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            'Form Type': root.dataset.mode,
            'Form ID': root.dataset.formId,
            'List ID': root.dataset.listId,
            'Signup Source': `Lisa Eleni dynamic ${root.dataset.mode} signup`,
            Language: root.dataset.language,
            Country: root.dataset.country,
            Currency: root.dataset.currency,
            'Selected Discount Percent': properties.signup_tier,
            'Minimum Spend': properties.signup_minimum_spend,
            'Confirmation State': 'pending_confirmation',
            'Page URL': pageUrl,
            'Submission ID': submissionId
          },
          time: occurredAt,
          unique_id: submissionId,
          metric: {
            data: {
              type: 'metric',
              attributes: {
                name: KLAVIYO_SIGNUP_EVENT
              }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email
              }
            }
          }
        }
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision: API_REVISION
        },
        body: JSON.stringify(payload),
        keepalive: true
      });

      if (!response.ok) {
        const retryable = response.status === 429 || response.status >= 500;

        if (retryable && attempt + 1 < KLAVIYO_EVENT_MAX_ATTEMPTS) {
          await wait(KLAVIYO_EVENT_RETRY_DELAY * 2 ** attempt);
          return trackKlaviyoSignup(
            root,
            email,
            properties,
            submissionId,
            occurredAt,
            pageUrl,
            attempt + 1
          );
        }

        root.dataset.klaviyoEventStatus = 'failed';
        console.error(`${KLAVIYO_SIGNUP_EVENT} rejected (${response.status})`);
        return false;
      }

      root.dataset.klaviyoEventTracked = 'true';
      root.dataset.klaviyoEventStatus = 'sent';
      return true;
    } catch (error) {
      if (attempt + 1 < KLAVIYO_EVENT_MAX_ATTEMPTS) {
        await wait(KLAVIYO_EVENT_RETRY_DELAY * 2 ** attempt);
        return trackKlaviyoSignup(
          root,
          email,
          properties,
          submissionId,
          occurredAt,
          pageUrl,
          attempt + 1
        );
      }

      root.dataset.klaviyoEventStatus = 'failed';
      console.error(`${KLAVIYO_SIGNUP_EVENT} failed after retries`);
      return false;
    }
  };

  const closePopup = (root, remember = true) => {
    root.hidden = true;
    document.body.classList.remove('le-signup-popup-open');
    if (remember) storageSet(DISMISS_KEY, String(Date.now()));
  };

  document.querySelectorAll('[data-le-signup]').forEach((root) => {
    const form = root.querySelector('[data-le-signup-form]');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (
        form.dataset.submitting === 'true' ||
        form.dataset.submitted === 'true'
      ) {
        return;
      }
      setStatus(root, '');

      if (!form.reportValidity()) return;

      const selectedTier = form.querySelector('input[name="signup_tier"]:checked');
      const email = form.elements.email.value.trim();
      const submitButton = form.querySelector('button[type="submit"]');
      const properties = signupProperties(
        root,
        selectedTier.value,
        selectedTier.dataset.minimumSpend
      );
      form.dataset.submitting = 'true';
      form.setAttribute('aria-busy', 'true');
      submitButton.disabled = true;

      try {
        await subscribe(root, email, properties);
        form.reset();
        form.dataset.submitted = 'true';
        root.dataset.signupState = 'success';
        const success = root.querySelector('[data-le-signup-success]');
        const successCopy = root.querySelector('[data-le-signup-success-copy]');
        const successTitle = success?.querySelector('.le-signup__success-title');
        if (successCopy) successCopy.textContent = message(root, 'successMessage');
        if (root.dataset.mode === 'popup' && successTitle?.id) {
          root.querySelector('.le-signup__panel')?.setAttribute('aria-labelledby', successTitle.id);
        }
        success?.focus({ preventScroll: true });
        storageSet(SUCCESS_KEY, String(Date.now()));
        publishAnalyticsEvent(
          'lisa_eleni:newsletter_form_submit',
          analyticsPayload(root, {
            signup_tier: Number(selectedTier.value),
            signup_minimum_spend: Number(selectedTier.dataset.minimumSpend)
          })
        );

        try {
          const canTrack = window.LisaEleniKlaviyo?.canTrack;
          if (typeof canTrack === 'function' && canTrack()) {
            const submissionId = createSubmissionId();
            const occurredAt = new Date().toISOString();
            const pageUrl = pageUrlWithoutQuery();
            root.dataset.klaviyoEventStatus = 'pending';
            void Promise.allSettled([
              identifySubscriber(email, properties),
              trackKlaviyoSignup(
                root,
                email,
                properties,
                submissionId,
                occurredAt,
                pageUrl
              )
            ]);
          } else {
            root.dataset.klaviyoEventStatus = 'skipped';
          }
        } catch {
          root.dataset.klaviyoEventStatus = 'failed';
          console.error('Klaviyo post-signup tracking could not start');
        }
      } catch (error) {
        console.error(error);
        setStatus(root, message(root, 'errorMessage'), 'error');
      } finally {
        delete form.dataset.submitting;
        form.removeAttribute('aria-busy');
        if (form.dataset.submitted !== 'true') submitButton.disabled = false;
      }
    });

    if (root.dataset.mode === 'popup') {
      root.querySelectorAll('[data-le-signup-close]').forEach((button) => {
        button.addEventListener('click', () => closePopup(root));
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !root.hidden) closePopup(root);
      });

      const dismissedAt = Number(storageGet(DISMISS_KEY) || 0);
      const hasCompleted = Boolean(storageGet(SUCCESS_KEY));
      const canOpen = !hasCompleted && Date.now() - dismissedAt > DISMISS_FOR;

      if (canOpen && !window.Shopify?.designMode) {
        window.setTimeout(() => {
          root.hidden = false;
          document.body.classList.add('le-signup-popup-open');
          trackFormView(root);
          root.querySelector('input[type="radio"]')?.focus();
        }, POPUP_DELAY);
      }
    } else if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          trackFormView(root);
          observer.disconnect();
        },
        { threshold: 0.5 }
      );
      observer.observe(root);
    } else {
      trackFormView(root);
    }
  });
})();
